import { EVENT_PROPERTY_KEY } from '~/lib/constants/event'
import { EventUploadButtonState } from '~/lib/models/event-upload-button-state'
import {
  MAX_UPLOAD_SIZE,
  PREMIUM_MAX_UPLOAD_SIZE,
  STARTER_MAX_UPLOAD_SIZE,
  VIDEO_MAX_UPLOAD_SIZE,
} from '~/lib/constants/upload-states'
import { GALLERY_SORTING_OPTIONS } from '~/lib/constants/images'
import { SUBSCRIPTION_PACKAGES } from '~/lib/constants/subscription-packages'

/**
 * @memberOf momentshare.models.event
 * @constructor
 */
export class Event {
  /**
   * @param {string} id
   * @param {string} name
   * @param {string} status
   * @param {Date} date
   * @param {string} shortLink
   * @param {Date} [publicationDate]
   * @param {Date} [publicationExpiresAt]
   * @param {string} safeName
   * @param {momentshare.models.event.EventProperty[]} [properties]
   * @param {QrStyle|null} [qrStyle] host-chosen QR style, null = default (see ~/lib/qr/qr-style)
   */
  constructor({
    id,
    name,
    status,
    date,
    publicationDate,
    publicationExpiresAt,
    safeName,
    properties,
    shortLink,
    albums,
    qrStyle,
  }) {
    this.id = id
    this.name = name
    this.status = status
    this.safeName = safeName
    this.date = date
    this.shortLink = shortLink
    this.properties = properties ?? []
    // Named filterable sub-collections. Plain objects: { id, name, slug, shortLink }.
    this.albums = albums ?? []
    this.qrStyle = qrStyle ?? null
    if (publicationDate) this.publicationDate = publicationDate
    if (publicationExpiresAt) this.publicationExpiresAt = publicationExpiresAt
  }
  // Starter carries hostingEnabled (so every released app shows its gallery)
  // but is capped at 100MB per file; the API's package-tier maxUploadSize uses
  // the same order (large videos, Starter, hosted, default).
  get maxFileSize() {
    if (this.hasLargeVideoUpgrade) {
      return VIDEO_MAX_UPLOAD_SIZE
    }

    if (this.isStarter) {
      return STARTER_MAX_UPLOAD_SIZE
    }

    if (this.hasMomentshareUpgrade) {
      return PREMIUM_MAX_UPLOAD_SIZE
    }

    return MAX_UPLOAD_SIZE
  }

  get formattedMaxFileSize() {
    if (this.hasLargeVideoUpgrade) {
      return '100GB'
    }

    if (this.isStarter) {
      return '100MB'
    }

    if (this.hasMomentshareUpgrade) {
      return '1000MB'
    }

    return '250MB'
  }

  // TODO: Remove in future
  get isLegacyEvent() {
    return !!this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.BACKGROUND_IMAGE_UPLOAD })
  }

  get isPublished() {
    return this.status === EVENT_STATES.PUBLISHED
  }

  get isEnded() {
    return this.status === EVENT_STATES.ENDED
  }

  get currentSubscriptionPackageId() {
    if (this.isDemo) {
      return SUBSCRIPTION_PACKAGES.DEMO
    }

    // Starter first: a legacy Basic+video event that upgraded to Starter
    // also has the video flag, but is Starter.
    if (this.isStarter) {
      return SUBSCRIPTION_PACKAGES.STARTER
    }

    if (this.hasAllUpgrades) {
      return SUBSCRIPTION_PACKAGES.UNLIMITED
    }

    if (this.hasMomentshareUpgrade) {
      return SUBSCRIPTION_PACKAGES.PREMIUM
    }

    return SUBSCRIPTION_PACKAGES.BASIC
  }

  // Starter (spec 2026-10-14) is hosted like Premium, restricted by the
  // starterPackage property. Mirrors the API's EventModel#isStarter; upgrading
  // to Premium/Unlimited clears starterPackage.
  get isStarter() {
    return !!(this.hasMomentshareUpgrade && this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.STARTER_PACKAGE }))
  }

  // Album creation is Premium/Unlimited only. The API answers 403
  // albums_require_upgrade for Basic, Starter and Demo events.
  get isAlbumsEntitled() {
    return !!(this.hasMomentshareUpgrade && !this.isStarter)
  }

  // Starter v2 (2026-10-16): no slideshow or casting on Starter. The API
  // answers 403 slideshow_requires_upgrade on the slideshow endpoint.
  get isSlideshowEntitled() {
    return !this.isStarter
  }

  get hasMomentshareUpgrade() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.HOSTING_ENABLED })
  }

  get isUsingGoogleDrive() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.USE_GOOGLE_DRIVE })
  }

  // True while the Drive→hosted media migration runs for this event (set by
  // the API on upgrade, cleared when the job ends). Gates the sync banner's
  // status polling so ordinary events never call /migration-status.
  get hasActiveMediaMigration() {
    return !!this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.MEDIA_MIGRATION_ACTIVE })
  }

  get isUsingDropbox() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.USE_DROPBOX })
  }

  // Media lives on Momentshare storage unless a personal connector moves it off.
  // Demo events have no hosting upgrade but are hosted by us all the same, so
  // reading hostingEnabled alone wrongly excludes them.
  get isUsingMomentshareStorage() {
    return !this.isUsingGoogleDrive && !this.isUsingDropbox && !!(this.hasMomentshareUpgrade || this.isDemo)
  }

  get hasLargeVideoUpgrade() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.LARGE_VIDEOS_ALLOWED })
  }

  get galleryIsEnabled() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.ENABLE_GALLERY })
  }

  get uploadingForGuestsEnabled() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.UPLOADING_FOR_GUESTS_ENABLED }) ?? true
  }

  // Absent → true: the property is only written once the owner touches the
  // toggle in the design editor, so untouched events show their button.
  get headerUploadButtonEnabled() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.HEADER_UPLOAD_BUTTON_ENABLED }) ?? true
  }

  // Guestbook (spec 2026-09-30). Off unless the host switched it on; the
  // sub-options default ON so a freshly enabled guestbook allows voice and
  // selfies without further clicks.
  get guestbookEnabled() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.GUESTBOOK_ENABLED }) === true
  }

  get guestbookVoiceEnabled() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.GUESTBOOK_VOICE_ENABLED }) ?? true
  }

  get guestbookSelfieEnabled() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.GUESTBOOK_SELFIE_ENABLED }) ?? true
  }

  get guestbookHiddenUntilReveal() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.GUESTBOOK_HIDDEN_UNTIL_REVEAL }) === true
  }

  // Mirrors the API's EventModel#isGuestbookEntitled: Premium and Unlimited
  // (hostingEnabled without starterPackage), and Demo, which is hosted by us
  // too. Starter is hosted but sold without the guestbook. isSponsored is
  // deliberately excluded: sponsored Basic events have no hosted storage.
  get isGuestbookEntitled() {
    return !!((this.hasMomentshareUpgrade && !this.isStarter) || this.isDemo)
  }

  get hasAllUpgrades() {
    return !!(this.hasMomentshareUpgrade && this.hasLargeVideoUpgrade)
  }

  get isDemo() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.IS_DEMO })
  }

  get demoLimitIsHit() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.DEMO_LIMIT_IS_HIT })
  }

  get commentsEnabled() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.COMMENTS_ENABLED }) ?? false
  }

  get isSponsored() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.IS_SPONSORED }) ?? false
  }

  // Mirrors the app's isEntitledToHostedApp gate (event_screen.dart): the app
  // shows events without one of these flags an upgrade wall instead of the
  // gallery, so don't advertise the app on events it cannot display.
  get isSupportedByApp() {
    return !!(this.hasMomentshareUpgrade || this.isDemo || this.isSponsored)
  }

  get defaultGallerySorting() {
    return (
      this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.DEFAULT_GALLERY_SORTING }) ?? GALLERY_SORTING_OPTIONS.DEFAULT
    )
  }

  get hasManualSortingActive() {
    return this.defaultGallerySorting === GALLERY_SORTING_OPTIONS.MANUAL
  }

  // Mirrors the API's extend-price: Basic and Starter extend per month,
  // Premium per six months, Unlimited per twelve.
  get extendableMonths() {
    if (this.hasLargeVideoUpgrade) return 12
    return this.hasMomentshareUpgrade && !this.isStarter ? 6 : 1
  }

  // Initial publication term in months, mirroring the API's order creation:
  // Basic and Starter 1, Premium 6, Unlimited 12.
  get usageTermMonths() {
    if (this.hasLargeVideoUpgrade) return 12
    return this.hasMomentshareUpgrade && !this.isStarter ? 6 : 1
  }

  // Design properties
  get backgroundImage() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.BACKGROUND_IMAGE })
  }

  get originalBackgroundImage() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.ORIGINAL_BACKGROUND_IMAGE })
  }

  get mobileBackgroundImage() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.BACKGROUND_IMAGE, mobile: true })
  }

  get originalMobileBackgroundImage() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.ORIGINAL_BACKGROUND_IMAGE, mobile: true })
  }

  get backgroundColor() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.BACKGROUND_COLOR })
  }

  get mobileBackgroundColor() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.BACKGROUND_COLOR, mobile: true })
  }

  get uploadText() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.UPLOAD_TEXT })
  }

  get mobileUploadText() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.UPLOAD_TEXT, mobile: true })
  }

  get textAlignment() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.UPLOAD_TEXT_ALIGNMENT })
  }

  get mobileTextAlignment() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.UPLOAD_TEXT_ALIGNMENT, mobile: true })
  }

  get font() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.FONT })
  }

  get mobileFont() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.FONT, mobile: true })
  }

  get textColor() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.TEXT_COLOR })
  }

  get mobileTextColor() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.TEXT_COLOR, mobile: true })
  }

  get buttonBackgroundColor() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.BUTTON_BACKGROUND_COLOR })
  }

  get mobileButtonBackgroundColor() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.BUTTON_BACKGROUND_COLOR, mobile: true })
  }

  get buttonTopPosition() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.BUTTON_TOP_POSITION, castBooleanToNumber: true })
  }

  get mobileButtonTopPosition() {
    return this.getPropertyValueByKey({
      key: EVENT_PROPERTY_KEY.BUTTON_TOP_POSITION,
      castBooleanToNumber: true,
      mobile: true,
    })
  }

  get buttonLeftPosition() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.BUTTON_LEFT_POSITION, castBooleanToNumber: true })
  }

  get mobileButtonLeftPosition() {
    return this.getPropertyValueByKey({
      key: EVENT_PROPERTY_KEY.BUTTON_LEFT_POSITION,
      castBooleanToNumber: true,
      mobile: true,
    })
  }

  get buttonFontSize() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.BUTTON_FONT_SIZE, castBooleanToNumber: true })
  }

  get mobileButtonFontSize() {
    return this.getPropertyValueByKey({
      key: EVENT_PROPERTY_KEY.BUTTON_FONT_SIZE,
      castBooleanToNumber: true,
      mobile: true,
    })
  }

  get buttonBorderRadius() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.BUTTON_BORDER_RADIUS, castBooleanToNumber: true })
  }

  get mobileButtonBorderRadius() {
    return this.getPropertyValueByKey({
      key: EVENT_PROPERTY_KEY.BUTTON_BORDER_RADIUS,
      castBooleanToNumber: true,
      mobile: true,
    })
  }

  get buttonBorderEnabled() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.BUTTON_BORDER_ENABLED })
  }

  get mobileButtonBorderEnabled() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.BUTTON_BORDER_ENABLED, mobile: true })
  }

  get buttonBorderWidth() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.BUTTON_BORDER_WIDTH, castBooleanToNumber: true })
  }

  get mobileButtonBorderWidth() {
    return this.getPropertyValueByKey({
      key: EVENT_PROPERTY_KEY.BUTTON_BORDER_WIDTH,
      castBooleanToNumber: true,
      mobile: true,
    })
  }

  get buttonBorderColor() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.BUTTON_BORDER_COLOR })
  }

  get mobileButtonBorderColor() {
    return this.getPropertyValueByKey({ key: EVENT_PROPERTY_KEY.BUTTON_BORDER_COLOR, mobile: true })
  }

  get uploadButtonState() {
    return new EventUploadButtonState({
      text: this.uploadText,
      font: this.font,
      backgroundColor: this.buttonBackgroundColor,
      color: this.textColor,
      top: this.buttonTopPosition,
      left: this.buttonLeftPosition,
      fontSize: this.buttonFontSize,
      borderRadius: this.buttonBorderRadius,
      border: this.buttonBorderEnabled,
      borderWidth: this.buttonBorderWidth,
      borderColor: this.buttonBorderColor,
      textAlignment: this.textAlignment,
    })
  }

  get mobileUploadButtonState() {
    return new EventUploadButtonState({
      text: this.mobileUploadText,
      font: this.mobileFont,
      backgroundColor: this.mobileButtonBackgroundColor,
      color: this.mobileTextColor,
      top: this.mobileButtonTopPosition,
      left: this.mobileButtonLeftPosition,
      fontSize: this.mobileButtonFontSize,
      borderRadius: this.mobileButtonBorderRadius,
      border: this.mobileButtonBorderEnabled,
      borderWidth: this.mobileButtonBorderWidth,
      borderColor: this.mobileButtonBorderColor,
      textAlignment: this.mobileTextAlignment,
    })
  }

  getPropertyValueByKey({ key, castBooleanToNumber = false, mobile = false }) {
    const valueKey = mobile ? 'mobileValue' : 'value'
    const value = this.properties.find((property) => property.key === key)?.[valueKey] ?? null

    if (!castBooleanToNumber) {
      return value
    }

    if (typeof value !== 'boolean') {
      return value
    }

    return value ? 1 : 0
  }
}

export const EVENT_STATES = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  ENDED: 'ended',
  DISABLED: 'disabled',
}
